import React from 'react';
import { Avatar } from '../../components/avatar';
import { Button } from '../../components/button';
import { getInitials } from '../../utils/utils';

interface FacilityContactCardProps {
  facilityContact: {
    id: string;
    contact: {
      id: string;
      first_name?: string;
      last_name?: string;
      email?: string;
      phone_number?: string;
      phone_number_extension?: string;
    };
    role?: string;
  };
  onEdit?: (facilityContact: FacilityContactCardProps['facilityContact']) => void;
  onDelete?: (facilityContact: FacilityContactCardProps['facilityContact']) => void;
  roleMap?: Record<string, string>;
  canEdit?: boolean;
}

export const FacilityContactCard: React.FC<FacilityContactCardProps> = ({
  facilityContact,
  onEdit,
  onDelete,
  roleMap = {},
  canEdit = false,
}) => {
  const fullName =
    [facilityContact.contact.first_name, facilityContact.contact.last_name]
      .filter(Boolean)
      .join(' ') || 'Unnamed Contact';
  const initials = getInitials(fullName);

  return (
    <div className="tw:relative tw:flex tw:flex-col tw:md:flex-row tw:items-start tw:md:items-center tw:bg-zinc-100 tw:px-3 tw:py-2 tw:rounded-lg tw:dark:border tw:dark:border-white/10 tw:dark:bg-white/5 tw:dark:shadow-none">
      {/* Avatar */}
      <Avatar
        initials={initials}
        className="tw:!hidden tw:md:!block tw:size-8 tw:mb-4 tw:md:mb-0 tw:text-white tw:bg-brand-green-0"
      />
      {/* Info */}
      <div className="tw:flex-grow tw:flex tw:flex-col tw:gap-1 tw:min-w-0 tw:mx-2">
        <div className="tw:text-xs tw:md:text-sm tw:text-zinc-700 tw:truncate">{fullName}</div>
        <div className="tw:mt-1 tw:flex tw:flex-col tw:md:flex-row tw:md:items-center tw:gap-0 tw:md:mt-0">
          {/* Role */}
          <div className="tw:md:w-1/3 tw:md:pr-2 tw:truncate tw:text-[0.625rem] tw:md:text-xs tw:text-zinc-400 tw:dark:text-zinc-300">
            {facilityContact.role ? (
              <span className="tw:truncate">
                {roleMap[facilityContact.role] || facilityContact.role}
              </span>
            ) : (
              <span className="tw:opacity-50">--</span>
            )}
          </div>
          {/* Email */}
          <div className="tw:md:w-1/3 tw:md:px-2 tw:truncate tw:text-[0.625rem] tw:md:text-xs tw:text-zinc-700 tw:dark:text-zinc-200">
            {facilityContact.contact.email ? (
              <a
                href={`mailto:${facilityContact.contact.email}`}
                className="tw:flex tw:items-center tw:truncate tw:!no-underline"
                title={`Email ${facilityContact.contact.email}`}
              >
                <i className="fa-solid fa-envelope tw:mr-2 tw:text-zinc-400 tw:dark:text-zinc-300" />
                <span className="tw:truncate">{facilityContact.contact.email}</span>
              </a>
            ) : (
              <span className="tw:opacity-50">--</span>
            )}
          </div>
          {/* Phone */}
          <div className="tw:md:w-1/3 tw:md:pl-2 tw:truncate tw:text-[0.625rem] tw:md:text-xs tw:text-zinc-700 tw:dark:text-zinc-200">
            {facilityContact.contact.phone_number ? (
              <a
                href={`tel:${facilityContact.contact.phone_number}`}
                className="tw:flex tw:items-center tw:truncate tw:!no-underline"
                title={`Call ${facilityContact.contact.phone_number}`}
              >
                <i className="fa-solid fa-phone tw:mr-2 tw:text-zinc-400 tw:dark:text-zinc-300" />
                <span className="tw:truncate">
                  {facilityContact.contact.phone_number}
                  {facilityContact.contact.phone_number_extension &&
                    ` ext. ${facilityContact.contact.phone_number_extension}`}
                </span>
              </a>
            ) : (
              <span className="tw:opacity-50">--</span>
            )}
          </div>
        </div>
      </div>
      {canEdit && (
        <div className="tw:absolute tw:top-4 tw:right-4 tw:flex tw:gap-4 tw:md:static tw:md:self-center tw:md:justify-end">
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(facilityContact)}
              className="tw:text-zinc-400 tw:hover:text-brand-green-0 tw:border-none"
              aria-label="Edit Contact"
            >
              <i className="fa-regular fa-pen-to-square" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(facilityContact)}
              className="tw:text-zinc-400 tw:hover:text-stoplight-danger tw:border-none"
              aria-label="Delete Contact"
            >
              <i className="fa-regular fa-trash" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
